/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';

import { BlIcon, BlIconType } from '@blend/shared-react';
import { mixins, colors as consumerColors, colors } from '@blend/consumer-components';
import { PreflightTestStatus } from './NetworkConnection';

export namespace DeviceCompatibilityStatus {
  export interface Props {
    preflightTestStatus: PreflightTestStatus;
    audioEnabled: boolean;
    videoEnabled: boolean;
  }
}

const styles = StyleSheet.create({
  deviceStatusWrapper: {
    ...mixins.margin(24, 0, 0, 0),
    padding: 16,
    borderRadius: 8,
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 500,
  },
  deviceStatusSupported: {
    backgroundColor: consumerColors.mint,
    color: 'white',
  },
  deviceStatusNotSupported: {
    backgroundColor: consumerColors.brickMinus4,
    color: 'white',
  },
  deviceStatusLoading: {
    backgroundColor: consumerColors.cloud20,
    color: colors.catalystLight80,
  },
});

export const DeviceCompatibilityStatus: React.FC<DeviceCompatibilityStatus.Props> = ({
  preflightTestStatus,
  audioEnabled,
  videoEnabled,
}) => {
  // Determine overall device compatibility status
  const getDeviceStatus = () => {
    if (preflightTestStatus === PreflightTestStatus.IN_PROGRESS) {
      return {
        status: 'Testing...',
        isSupported: null,
        style: styles.deviceStatusLoading,
        icon: 'loading',
      };
    }

    const testSuccess = preflightTestStatus === PreflightTestStatus.SUCCESS;
    const allTestsPassed = testSuccess && audioEnabled && videoEnabled;

    if (allTestsPassed) {
      return {
        status: 'Supported',
        isSupported: true,
        style: styles.deviceStatusSupported,
        icon: 'success',
      };
    } else {
      return {
        status: 'Not supported',
        isSupported: false,
        style: styles.deviceStatusNotSupported,
        icon: 'error',
      };
    }
  };

  const deviceStatus = getDeviceStatus();

  return (
    <div className={css(styles.deviceStatusWrapper, deviceStatus.style)}>
      <BlIcon icon={deviceStatus.icon as BlIconType} />
      <span style={{ marginLeft: 8 }}>
        Device Compatibility: <strong>{deviceStatus.status}</strong>
      </span>
    </div>
  );
};
